import { ShieldCheck, Lock, Server, Globe, Clock, FileCheck, Key, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { TrustSignal } from "@/data/trustSignals";
import { Medallion, type MedallionAccent } from "@/components/brand/Medallion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const MEDALLION_MAP: Record<string, { name: string; subLabel?: string; accent: MedallionAccent }> = {
  gdpr:       { name: "GDPR",      accent: "blue" },
  soc2:       { name: "SOC 2",     subLabel: "Type II",    accent: "blue" },
  iso27001:   { name: "ISO 27001", accent: "green" },
  hipaa:      { name: "HIPAA",     accent: "red" },
  "pci-dss":  { name: "PCI DSS",   subLabel: "v4.0",       accent: "navy" },
  "nist-csf": { name: "NIST CSF",  accent: "purple" },
  dora:       { name: "DORA",      accent: "orange" },
  nis2:       { name: "NIS2",      accent: "teal" },
  ccpa:       { name: "CCPA",      accent: "violet" },
};

const ICONS = {
  "shield-check": ShieldCheck,
  lock: Lock,
  server: Server,
  globe: Globe,
  clock: Clock,
  "file-check": FileCheck,
  key: Key,
} as const;

interface TrustBadgeProps {
  signal: TrustSignal;
  /** compact = pill chip, full = card with description, logo = artwork tile,
   *  strip = bare mark plus label, for the dark certification strip */
  variant?: "compact" | "full" | "logo" | "strip";
  className?: string;
}

const TrustBadge = ({ signal, variant = "compact", className }: TrustBadgeProps) => {
  const { t } = useTranslation();
  const Icon = ICONS[signal.icon];
  const label = t(signal.labelKey);
  const desc = t(signal.descKey);

  const statusLabel =
    signal.status === "verified"
      ? t("trustCenter.status.verified")
      : signal.status === "in-progress"
      ? t("trustCenter.status.inProgress")
      : t("trustCenter.status.aligned");

  const statusStyles =
    signal.status === "verified"
      ? "bg-primary/10 text-primary border-primary/30"
      : signal.status === "in-progress"
      ? "bg-muted text-muted-foreground border-border"
      : "bg-secondary text-secondary-foreground border-border";

  // Bare mark and label, no card chrome. Sized and coloured for a Brand Ink
  // ground, so it inherits nothing from the light-theme card tokens.
  if (variant === "strip") {
    const isInProgress = signal.status === "in-progress";
    const med = MEDALLION_MAP[signal.id];
    return (
      <div
        className={cn(
          "group flex flex-col items-center gap-1.5 px-2 py-1",
          className
        )}
        title={isInProgress && signal.targetDate ? `${label} · ${statusLabel} ${signal.targetDate}` : label}
      >
        {med ? (
          <Medallion
            name={med.name}
            subLabel={med.subLabel}
            accent={med.accent}
            size={34}
            variant="card"
            className={cn("transition-opacity", isInProgress ? "opacity-70" : "opacity-95")}
          />
        ) : signal.badgeImage ? (
          <img
            src={signal.badgeImage}
            alt=""
            width={34}
            height={34}
            loading="lazy"
            decoding="async"
            className={cn("h-[26px] w-[26px] transition-opacity", isInProgress ? "opacity-70" : "opacity-95")}
          />
        ) : (
          <Icon className="h-5 w-5 text-trail-teal" aria-hidden />
        )}
        <span className="text-[10px] font-medium leading-tight text-brand-ink-foreground/70">
          {label}
        </span>
        {/* The status is NOT optional decoration. A bare "SOC 2 Type II" badge
            reads as an attestation this company holds, and it does not hold one
            yet — that is the exact claim the brand rules forbid. The strip shows
            the status inline rather than only in a title attribute. */}
        {isInProgress && (
          <span className="font-mono text-[9px] uppercase leading-none tracking-wide text-trail-teal">
            {statusLabel}
            {signal.targetDate ? ` · ${signal.targetDate}` : ""}
          </span>
        )}
      </div>
    );
  }

  if (variant === "logo") {
    const isInProgress = signal.status === "in-progress";
    const med = MEDALLION_MAP[signal.id];
    return (
      <div
        className={cn(
          "group relative flex flex-col items-center gap-2 p-3 border bg-card transition-all hover:shadow-md hover:border-primary/40",
          className
        )}
      >
        {med ? (
          <Medallion
            name={med.name}
            subLabel={med.subLabel}
            accent={med.accent}
            size={56}
            variant="card"
            className={cn(
              "transition-all",
              isInProgress && "grayscale opacity-70 group-hover:opacity-90"
            )}
          />
        ) : signal.badgeImage ? (
          <img
            src={signal.badgeImage}
            alt={`${label} badge`}
            width={56}
            height={56}
            loading="lazy"
            decoding="async"
            className={cn(
              "w-10 h-10 transition-all",
              isInProgress && "grayscale opacity-60 group-hover:opacity-80"
            )}
          />
        ) : (
          <Icon className="w-8 h-8 text-primary" aria-hidden />
        )}
        <div className="text-center">
          <div className="text-xs font-semibold text-foreground leading-tight">{label}</div>
          {isInProgress && (
            <div className="mt-1">
              <span
                className={cn(
                  "inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-medium uppercase tracking-wide",
                  statusStyles
                )}
              >
                <Clock className="w-2.5 h-2.5" aria-hidden />
                {statusLabel}
                {signal.targetDate ? ` · ${signal.targetDate}` : ""}
              </span>
            </div>
          )}
        </div>
      </div>
    );

  }

  if (variant === "compact") {
    const tooltipText =
      signal.status === "in-progress" && signal.targetDate
        ? `${statusLabel} · ${signal.targetDate}`
        : statusLabel;

    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors hover:border-primary/50",
                statusStyles,
                className
              )}
            >
              <Icon className="w-3.5 h-3.5" aria-hidden />
              <span>{label}</span>
              {signal.status === "verified" && <Check className="w-3 h-3" aria-hidden />}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs">
              <span className="font-semibold">{tooltipText}</span> — {desc}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-2 p-4 rounded-lg border bg-card transition-shadow hover:shadow-sm",
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-primary flex-shrink-0" aria-hidden />
          <h3 className="text-sm font-semibold text-foreground">{label}</h3>
        </div>
        <span
          className={cn(
            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium uppercase tracking-wide",
            statusStyles
          )}
        >
          {signal.status === "verified" && <Check className="w-2.5 h-2.5" aria-hidden />}
          {signal.status === "in-progress" && <Clock className="w-2.5 h-2.5" aria-hidden />}
          {statusLabel}
        </span>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
      {signal.targetDate && (
        <p className="text-xs text-foreground/70">
          <span className="font-medium">{t("trustCenter.targetLabel")}:</span> {signal.targetDate}
        </p>
      )}
    </div>
  );
};

export default TrustBadge;
